import { Subscription } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { DataOperation } from "@/fhir/enums/fhir-valuesets";

import { COMMUNICATION_THREAD_CATEGORY } from "../constants/messages";

/**
 * Criteria for thread messages sent by anyone but the current user.
 *
 * @param profileReference - the current user, as `Practitioner/<id>` or `Patient/<id>`.
 * @param recipientReferences - each one adds a `recipient` param. Repeated FHIR search params are
 * ANDed, so a message must go to every listed recipient.
 * @returns the Communication subscription criteria.
 */
export function threadReplyCriteria(
  profileReference: string,
  recipientReferences: string[] = [],
): string {
  const recipientParams = recipientReferences.map((reference) => `&recipient=${reference}`);

  return (
    `Communication?category=${COMMUNICATION_THREAD_CATEGORY}&part-of:missing=false&` +
    `sender:not=${profileReference}${recipientParams.join("")}`
  );
}

/**
 * Subscription for the read receipts the current user writes on thread messages.
 *
 * @param profileReference - the current user, as `Practitioner/<id>` or `Patient/<id>`.
 * @returns the Provenance criteria and the FHIRPath filter that keeps only Communication reads.
 */
export function readProvenanceSubscription(profileReference: string): {
  criteria: string;
  subscriptionProps: Partial<Subscription>;
} {
  return {
    criteria: `Provenance?agent=${profileReference}`,
    subscriptionProps: {
      extension: [
        {
          url: Extensions.FhirPathCriteriaExpression,
          valueString:
            'target.exists(reference.startsWith("Communication/")) ' +
            `and activity.coding.where(code = "${DataOperation.Read}").exists()`,
        },
      ],
    },
  };
}
